// 게스트 링크 시트 — 운영진이 일정의 공유용 게스트 링크를 발급/공유/삭제.
//
// 발급 직후에만 plain token을 받을 수 있고 (서버는 해시만 보관) 그 후 시트를
// 다시 열면 link 자체를 재구성할 수 없어 "발급된 링크가 있어요" 표시만 가능.
// 컴포넌트는 fetch → status 표시 → 액션(issue/share/copy/delete)을 useState로
// 흐름 관리.

import { Fragment } from "preact";
import { useEffect, useState } from "preact/hooks";
import { api } from "../api";
import { SHARE_ORIGIN, state } from "../state";
import { Icon } from "../ui/components/Icon";
import { formatGuestExpiresAt } from "../ui/format";
import { mountPreact } from "../ui/preact-mount";
import { closeSheet, openSheet, toast } from "../ui/sheet";

interface GuestLinkStatus {
  has_active_token?: boolean;
  expires_at?: string | null;
  issued_at?: string | null;
}

interface GuestLinkBodyProps {
  ev: any;
}

function GuestLinkBody({ ev }: GuestLinkBodyProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<GuestLinkStatus | null>(null);
  const [plainToken, setPlainToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await api(`/api/v1/events/${ev.event_id}/guest-link`);
        if (cancelled) return;
        setStatus(s);
        setPlainToken(s?.token || null);
      } catch (e: any) {
        if (cancelled) return;
        setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function issueLink() {
    if (busy) return;
    setBusy(true);
    try {
      const issued = await api(`/api/v1/events/${ev.event_id}/guest-link`, { method: "POST" });
      setStatus({ has_active_token: true, expires_at: issued.expires_at, issued_at: issued.issued_at });
      setPlainToken(issued.token);
    } catch (err: any) {
      toast(err.message || "발급 실패");
    } finally {
      setBusy(false);
    }
  }

  async function deleteLink() {
    if (!confirm("게스트 링크를 삭제할까요? 링크를 받은 사람은 더 이상 접근할 수 없어요.")) return;
    if (busy) return;
    setBusy(true);
    try {
      await api(`/api/v1/events/${ev.event_id}/guest-link`, { method: "DELETE" });
      setStatus({ has_active_token: false });
      setPlainToken(null);
      toast("게스트 링크를 삭제했어요");
    } catch (err: any) {
      toast(err.message || "삭제 실패");
    } finally {
      setBusy(false);
    }
  }

  const shareUrl = (status?.has_active_token && plainToken)
    ? `${SHARE_ORIGIN}/c/${state.config.clubSlug}/guest/events/${ev.event_id}?token=${plainToken}`
    : null;

  async function shareLink() {
    if (!shareUrl) return;
    try {
      await (navigator as any).share({ title: ev.title || "게스트 링크", url: shareUrl });
      closeSheet();
    } catch (e: any) {
      if (e.name !== "AbortError") toast("공유에 실패했어요");
    }
  }

  async function copyLink() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast("링크를 복사했어요");
    } catch { toast("복사에 실패했어요"); }
  }

  if (loading) return <div class="stack"><div class="loading">불러오는 중…</div></div>;
  if (error) return <div class="stack"><div class="error-card">{error}</div></div>;

  const hasActive = !!status?.has_active_token;
  const canShare = typeof navigator !== "undefined" && typeof (navigator as any).share === "function";

  return (
    <div class="stack">
      <p class="field__hint">
        대진표/결과만 볼 수 있는 공유 링크예요. 이름·성별 외 개인 정보는 노출되지 않아요.
      </p>
      {!hasActive ? (
        <button
          class="btn btn--primary btn--block"
          type="button"
          disabled={busy}
          onClick={issueLink}
        >
          게스트 링크 발급
        </button>
      ) : (
        <Fragment>
          {shareUrl && canShare && (
            <button class="btn btn--primary btn--block" type="button" onClick={shareLink}>
              <Icon name="share" size={16} /> 공유하기
            </button>
          )}
          {shareUrl && (
            <button
              class={canShare ? "btn btn--secondary btn--block" : "btn btn--primary btn--block"}
              type="button"
              style="margin-top:6px"
              onClick={copyLink}
            >
              링크 복사
            </button>
          )}
          {status?.expires_at && (
            <p class="field__hint" style="margin-top:12px">
              만료: {formatGuestExpiresAt(status.expires_at)}
            </p>
          )}
          <button
            class="btn btn--ghost btn--block"
            type="button"
            style="margin-top:12px"
            disabled={busy}
            onClick={deleteLink}
          >
            링크 삭제
          </button>
        </Fragment>
      )}
    </div>
  );
}

export function openGuestLinkSheet(ev: any): void {
  openSheet("게스트 링크", mountPreact(<GuestLinkBody ev={ev} />));
}
